/**
 * Phone number formatting utilities for E.164 display.
 * Simple regex-based approach for German phone numbers (+49).
 * No external library dependencies.
 */

/** Default country code (Germany) */
const DEFAULT_COUNTRY_CODE = "49";

/**
 * Known German area codes (with leading 0) for display grouping.
 * Sorted longest-first so e.g. "0221" matches before "02".
 */
const GERMAN_AREA_CODES: readonly string[] = [
  // 4-digit area codes
  "0221",
  "0228",
  "0231",
  "0241",
  "0251",
  "0341",
  "0351",
  "0361",
  "0371",
  "0381",
  "0391",
  "0421",
  "0431",
  "0451",
  "0461",
  "0511",
  "0521",
  "0531",
  "0541",
  "0551",
  "0561",
  "0571",
  "0611",
  "0621",
  "0631",
  "0651",
  "0681",
  "0711",
  "0721",
  "0731",
  "0741",
  "0751",
  "0761",
  "0771",
  "0821",
  "0831",
  "0841",
  "0851",
  "0861",
  "0871",
  "0881",
  "0911",
  "0921",
  "0931",
  "0941",
  "0951",
  "0961",
  "0971",
  "0981",
  // 3-digit area codes (major cities)
  "030",
  "040",
  "069",
  "089",
];

/** Options for normalizeToE164 */
export interface NormalizeOptions {
  /** Country code without '+', e.g. '49' for Germany */
  countryCode?: string;
  /**
   * Trunk access digit (Amtskennziffer), typically '0'.
   * When set, this prefix is stripped before applying normal detection.
   * Used when automatic Amtsholung is disabled on the PBX.
   * Not wired to UI yet - reserved for future use.
   */
  amtskennziffer?: string;
}

/**
 * Checks whether a number string looks like an internal PBX extension.
 * Any number that does NOT start with '0' or '+' is considered internal,
 * regardless of length. Star codes (*) and hash codes (#) are always internal.
 */
export function isInternalExtension(number: string): boolean {
  if (!number) return false;
  const cleaned = number.replace(/[\s\-().]/g, "");
  if (cleaned.startsWith("*") || cleaned.startsWith("#")) return true;
  if (cleaned.startsWith("+") || cleaned.startsWith("0")) return false;
  const digitsOnly = cleaned.replace(/\D/g, "");
  return digitsOnly.length > 0;
}

/**
 * Strips all formatting characters, returning only digits and optionally
 * a leading '+'.
 */
export function stripFormatting(number: string): string {
  if (!number) return "";
  const hasPlus = number.trimStart().startsWith("+");
  const digits = number.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}

/**
 * Normalizes a phone number to E.164 format: +[country][subscriber]
 *
 * Handles these input formats:
 *   "030 123456"       -> "+4930123456"
 *   "004930123456"     -> "+4930123456"
 *   "+4930123456"      -> "+4930123456"  (already E.164)
 *   "+49 30 123456"    -> "+4930123456"  (formatted E.164)
 *   "123456"           -> "123456"       (internal extension, returned as-is)
 *
 * Internal extensions (no '0' or '+' prefix, star/hash codes) are returned as-is.
 * E.164 normalization is only applied when the number starts with '0' or '+'.
 */
export function normalizeToE164(
  input: string,
  options?: NormalizeOptions,
): string {
  if (!input) return "";

  const trimmed = input.trim();
  if (isInternalExtension(trimmed)) return trimmed;

  const countryCode = options?.countryCode ?? DEFAULT_COUNTRY_CODE;
  const amtskennziffer = options?.amtskennziffer;

  // Remove redundant national trunk prefix in parentheses, e.g.:
  // "+49 (0)30 123456" or "+49 (030) 123456" -> "+49 30 123456"
  let cleaned = trimmed.replace(/\(0\d*\)\s*/, (match, offset, str) => {
    // Only strip if there's a '+' country code before it
    const before = str.substring(0, offset).trim();
    if (/^\+\d+/.test(before)) {
      // Extract digits after the leading '0' inside parens, e.g. "(030)" -> "30"
      const innerDigits = match
        .replace(/[()]/g, "")
        .replace(/^\s*0/, "")
        .trim();
      return innerDigits;
    }
    return match;
  });

  // Strip all non-digit characters except leading '+'
  const hasPlus = cleaned.startsWith("+");
  let digits = cleaned.replace(/\D/g, "");

  if (!digits) return trimmed;

  // Already has '+' prefix: assume full international number
  if (hasPlus) {
    return `+${digits}`;
  }

  // If Amtskennziffer is configured, strip it before further detection
  if (amtskennziffer && digits.startsWith(amtskennziffer)) {
    digits = digits.substring(amtskennziffer.length);
    if (!digits) return trimmed;
  }

  // Starts with '00' (international prefix): replace with '+'
  if (digits.startsWith("00")) {
    return `+${digits.substring(2)}`;
  }

  // Starts with '0' (national format): replace leading 0 with country code
  if (digits.startsWith("0")) {
    return `+${countryCode}${digits.substring(1)}`;
  }

  // If we reach here, the number doesn't start with '+' or '0'.
  // isInternalExtension should have caught this, but return as-is as a safeguard.
  return trimmed;
}

/**
 * Formats an E.164 number for human-readable display.
 *
 * For German numbers (+49), attempts to detect known area codes:
 *   "+4930123456"   -> "+49 30 123456"
 *   "+491701234567" -> "+49 170 1234567"  (mobile)
 *
 * For unknown area codes or non-German numbers:
 *   "+49123456"     -> "+49 123456"       (fallback)
 *   "+33123456789"  -> "+33 123456789"
 *
 * Internal extensions are returned as-is.
 */
export function formatForDisplay(number: string): string {
  if (!number) return "";

  const trimmed = number.trim();
  if (isInternalExtension(trimmed)) return trimmed;

  // Normalize first
  const e164 = normalizeToE164(trimmed);
  if (!e164.startsWith("+")) return trimmed;

  const digits = e164.substring(1); // Remove '+'

  // German number detection
  if (digits.startsWith("49")) {
    const nationalNumber = digits.substring(2);

    // Try to match known area codes (compare without leading '0')
    for (const areaCode of GERMAN_AREA_CODES) {
      const areaWithout0 = areaCode.substring(1);
      if (nationalNumber.startsWith(areaWithout0)) {
        const subscriber = nationalNumber.substring(areaWithout0.length);
        return `+49 ${areaWithout0} ${subscriber}`;
      }
    }

    // German mobile numbers: 15x, 16x, 17x (3-digit prefix)
    const mobileMatch = nationalNumber.match(/^(1[567]\d)(\d+)$/);
    if (mobileMatch) {
      return `+49 ${mobileMatch[1]} ${mobileMatch[2]}`;
    }

    // Fallback: +49 + rest as one block
    return `+49 ${nationalNumber}`;
  }

  // Non-German numbers: space after first 2 digits (approximate country code)
  if (digits.length > 2) {
    return `+${digits.substring(0, 2)} ${digits.substring(2)}`;
  }

  return e164;
}

/**
 * Converts a formatted/E.164 number to a dialable string for SIP.
 * Strips '+' and formatting characters. The SIP server expects raw digits.
 *
 * "+49 30 123456" -> "4930123456"
 * "+4930123456"   -> "4930123456"
 * "123"           -> "123" (extension, unchanged)
 * "*8200"         -> "*8200" (star code, unchanged)
 */
export function toDialable(number: string): string {
  if (!number) return "";
  const trimmed = number.trim();
  if (isInternalExtension(trimmed)) {
    return trimmed.replace(/[\s]/g, "");
  }
  return trimmed.replace(/[+\s\-().]/g, "");
}
